import { useEffect, useRef } from 'react';
import { aoiPaths, aoiPoints } from '../../../common/aoi';

// Space reserved for the floating UI cards overlaid on each side of the map,
// so the fitted ROI lands in the visible area between them.
const DEFAULT_FIT_PADDING = { top: 80, bottom: 80, left: 360, right: 360 };

/**
 * Draws a region-of-interest polygon on a Google Map and keeps it in sync
 * with the given coordinates: (re)draws when they change, fits the viewport
 * to the polygon (with padding for the overlaid UI cards) and removes the
 * polygon when the page unmounts.
 *
 * Returns the map ref, the polygon ref (for consumers that hit-test against
 * the ROI, e.g. baseline-inside-AOI checks), the `onMapLoad` callback to
 * pass to `GoogleMap`'s `onLoad`, and `fitRoi` for framing the area again on
 * demand - which the PDF report needs before it captures the map, since the
 * user may have panned away from the analysis.
 * @param {Array|null} coordinates polygon path as `[lng, lat]` pairs, or an area of several polygons
 * @param {string} strokeColor
 * @param {{top: number, bottom: number, left: number, right: number}} [fitPadding]
 * @returns {{mapRef: object, roiRef: object, onMapLoad: Function, fitRoi: Function}}
 */
export const useRoiOverlay = (coordinates, strokeColor, fitPadding = DEFAULT_FIT_PADDING) => {
  const mapRef = useRef();
  const roiRef = useRef();

  const drawRoi = () => {
    if (!mapRef.current || !coordinates || !coordinates.length) return;

    roiRef.current?.setMap(null);

    roiRef.current = new window.google.maps.Polygon({
      paths: aoiPaths(coordinates),
      geodesic: true,
      fillColor: 'transparent',
      strokeColor,
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clickable: false,
    });
    roiRef.current.setMap(mapRef.current);

    fitRoi();
  };

  /**
   * Frames the map on the area. Separate from drawing it so the view can be
   * restored without redrawing the polygon.
   * @returns {boolean} Whether there was an area to frame.
   */
  const fitRoi = () => {
    if (!mapRef.current || !coordinates?.length) return false;
    const bounds = new window.google.maps.LatLngBounds();
    aoiPoints(coordinates).forEach(([lng, lat]) => bounds.extend({ lat, lng }));
    mapRef.current.fitBounds(bounds, fitPadding);
    return true;
  };

  // Redraw when the coordinates change after the map has already loaded.
  useEffect(() => {
    drawRoi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);

  useEffect(() => () => roiRef.current?.setMap(null), []);

  const onMapLoad = (map) => {
    mapRef.current = map;
    drawRoi();
  };

  return { mapRef, roiRef, onMapLoad, fitRoi };
};
